import React, { useEffect } from 'react';
import dayjs from 'dayjs';

import { GetParticipantAccountActivityReportSummaryPlanYearPeriodEnum } from '@/dtos';
import { useFilters } from '@/utils';

import { useFavoriteReportHandler } from '../../hooks';
import { ReportDetailsProps, ReportTab, ReportTypeKeys } from '../../report.types';
import { useGetReportEmployerId, useMultipleEmployerSelectAvailable } from '../hooks';
import ReportDetailsBase from '../ReportDetailsBase';

import {
  ParticipantAccountActivityReportTableParams,
  ParticipantAccountActivitySummaryReportParams,
} from './ParticipantActivityReport.types';
import ParticipantActivityReportSummary from './ParticipantActivityReportSummary';
import ParticipantActivityReportTable from './ParticipantActivityReportTable';

type FiltersValues = ParticipantAccountActivityReportTableParams &
  ParticipantAccountActivitySummaryReportParams & {
    tab: ReportTab;
  };

// const TEST_ID = 'participant-activity-report';
interface Props extends ReportDetailsProps<FiltersValues> {}
const ParticipantAccountActivityReportDetails: React.FC<Props> = ({
  defaultEmployerId,
  defaultTab,
  isPreviewMode,
  onChangeFilters,
  defaultFilterValues,
}) => {
  const employerId = useGetReportEmployerId();
  const { filtersValues, setFilterValue } = useFilters<FiltersValues>(
    {
      tab: defaultTab || ReportTab.Summary,
      employerId: defaultEmployerId || employerId || '',
      employerIds: defaultEmployerId || employerId ? [employerId] : [],
      accountTypes: [],
      searchString: '',
      isSSNShowed: false,
      enrollmentStatuses: [],
      asDate: dayjs().startOf('day').toDate(),
      reportPlanIds: [],
      planYearPeriod: [],
      planIds: [],
      ...defaultFilterValues,
    },
    {
      disabled: isPreviewMode,
      autoClear: true,
    }
  );

  useEffect(() => {
    if (isPreviewMode) {
      return;
    }

    setFilterValue.accountTypes([]);
    setFilterValue.enrollmentStatuses([]);
    setFilterValue.planIds([]);
    if (isMultipleEmployerAvailable) {
      setFilterValue.planYearPeriod([GetParticipantAccountActivityReportSummaryPlanYearPeriodEnum.CURRENT]);
    } else {
      setFilterValue.planYearPeriod([]);
    }
    setFilterValue.reportPlanIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersValues.employerId, filtersValues.employerIds?.join(',')]);
  useEffect(() => {
    if (isPreviewMode) {
      return;
    }
    if (employerId && !filtersValues.employerId) {
      setFilterValue.employerId(employerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employerId]);

  const isMultipleEmployerAvailable = useMultipleEmployerSelectAvailable();

  useEffect(() => {
    if (isMultipleEmployerAvailable) {
      setFilterValue.planYearPeriod([GetParticipantAccountActivityReportSummaryPlanYearPeriodEnum.CURRENT]);
    } else {
      setFilterValue.planYearPeriod([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultipleEmployerAvailable]);
  useEffect(() => {
    if (defaultTab) {
      setFilterValue.tab(defaultTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTab]);
  useEffect(() => {
    if (isPreviewMode && onChangeFilters) {
      onChangeFilters(filtersValues);
    }
  }, [filtersValues, isPreviewMode, onChangeFilters]);
  useEffect(() => {
    if (!isPreviewMode) {
      setFilterValue.reportPlanIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersValues.employerId]);
  const { isFavorite, handleToggleReportKey } = useFavoriteReportHandler(ReportTypeKeys.ParticipantActivity);
  return (
    <ReportDetailsBase
      isPreviewMode={isPreviewMode}
      isFavorite={isFavorite}
      onToggleFavorite={handleToggleReportKey}
      tab={filtersValues.tab}
      onSetTab={setFilterValue.tab}
      title="Participant Account Activity"
    >
      {filtersValues.tab === ReportTab.Report ? (
        <ParticipantActivityReportTable
          defaultEmployerId={defaultEmployerId}
          isPreviewMode={isPreviewMode}
          key={filtersValues.tab}
          filtersValues={filtersValues}
          onSetFilterValue={setFilterValue}
        />
      ) : (
        <ParticipantActivityReportSummary
          defaultEmployerId={defaultEmployerId}
          isPreviewMode={isPreviewMode}
          filtersValues={filtersValues}
          onSetFilterValue={setFilterValue}
        />
      )}
    </ReportDetailsBase>
  );
};

export default ParticipantAccountActivityReportDetails;
